import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import { getUserIdFromToken, isAdminToken } from "../utils/jwt";
import * as authApi from "../api/authApi";

const AuthContext = createContext(null);

const TOKEN_KEY = "tl_token";

export function AuthProvider({ children }) {
  const [token, setTokenState] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [bootstrapped, setBootstrapped] = useState(false);

  useEffect(() => {
    setBootstrapped(true);
  }, []);

  const setToken = useCallback((t) => {
    setTokenState(t);
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  }, []);

  const userId = useMemo(() => (token ? getUserIdFromToken(token) : null), [token]);

  const isAdmin = useMemo(() => Boolean(token && isAdminToken(token)), [token]);

  const login = useCallback(
    async (email, password) => {
      const jwt = await authApi.login(email, password);
      const t = typeof jwt === "string" ? jwt : jwt?.token ?? jwt;
      if (!t) throw new Error("Invalid login response");
      setToken(t);
      return t;
    },
    [setToken]
  );

  const register = useCallback(async (email, password) => {
    await authApi.register(email, password);
  }, []);

  const logout = useCallback(() => {
    setToken(null);
  }, [setToken]);

  const value = useMemo(
    () => ({
      token,
      userId,
      isAdmin,
      isAuthenticated: Boolean(token && userId),
      bootstrapped,
      login,
      register,
      logout,
      setToken,
    }),
    [token, userId, isAdmin, bootstrapped, login, register, logout, setToken]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
